import { Drawer, Paper } from '@mantine/core';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';
import { darkModeRoot } from 'src/app/App.css';
import { OkButton } from 'src/components/Buttons/OkButton';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui/Stack';
import { ListingTab } from 'src/modals/ListingDetails/components/listingDetailsUtils';
import {
  ListingDetailsModalContextProvider,
  useListingDetailsContext,
} from 'src/modals/ListingDetails/ListingDetailsModalV2Context';
import { AutoPricingSidePanelContextProvider } from 'src/modals/ListingDetails/ListingForms/AutoPricingSidePanel';
import { ChevronRightIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import * as drawerStyles from '../MobileListingDrawer.css';
import { MobileListingDrawerZIndex } from '../MobileListingDrawerContext.constants';
import * as styles from './MobileListingSideView.css';
import { useListingTileActions } from './MobileListingSideView.hooks';

export const MobileListingSideView = () => {
  const [showSectionView, toggleShowSectionView] = useToggle(false);

  return (
    <AutoPricingSidePanelContextProvider>
      <ListingDetailsModalContextProvider>
        <Stack height="full" width="full" direction="column" gap="l">
          <MobileListingSectionalAccordion
            toggleShowSectionView={toggleShowSectionView}
          />
          <MobileListingSectionalDrawer
            showSectionView={showSectionView}
            onCloseSectionView={toggleShowSectionView}
          />
        </Stack>
      </ListingDetailsModalContextProvider>
    </AutoPricingSidePanelContextProvider>
  );
};

const MobileListingSectionalAccordion = ({
  toggleShowSectionView,
}: {
  toggleShowSectionView: () => void;
}) => {
  const { tabs, onTabChange } = useListingDetailsContext();

  const listingTileActions = useListingTileActions();

  const onSectionClick = useCallback(
    (tabValue: ListingTab) => {
      onTabChange(tabValue);
      toggleShowSectionView();
    },
    [onTabChange, toggleShowSectionView]
  );

  return (
    <Stack
      direction="column"
      width="full"
      height="full"
      alignItems="center"
      justifyContent="spaceBetween"
      className={drawerStyles.drawerInnerContent}
      style={{ paddingBottom: vars.spacing.m }}
    >
      <Stack direction="column" width="full" alignItems="center" gap="s">
        {tabs.map((tab, index) => {
          return (
            <Stack
              role="button"
              tabIndex={index}
              key={tab.value}
              width="full"
              justifyContent="spaceBetween"
              alignItems="center"
              className={styles.sectionalItemContainer}
              onClick={() => onSectionClick(tab.value as ListingTab)}
            >
              {tab.title}
              <ChevronRightIcon size={vars.iconSize.s} />
            </Stack>
          );
        })}
      </Stack>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap="m"
      >
        {listingTileActions.map((action, index) => {
          return (
            <Stack
              role="button"
              tabIndex={index}
              key={index}
              direction="column"
              gap="s"
              alignItems="center"
              justifyContent="center"
              onClick={action.onClick}
              className={styles.listingTileActionContainer}
            >
              {action.icon}
              {action.label}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const MobileListingSectionalDrawer = ({
  showSectionView,
  onCloseSectionView,
}: {
  showSectionView: boolean;
  onCloseSectionView: () => void;
}) => {
  const { isDarkMode } = useSiteTheme();
  const { tabs, activeTabIndex, onSubmit, hasChanges, isLoading } =
    useListingDetailsContext();

  const activeTab = useMemo(() => tabs[activeTabIndex], [activeTabIndex, tabs]);

  if (!activeTab) {
    return null;
  }

  return (
    <Drawer.Root
      opened={!!activeTab && showSectionView}
      onClose={onCloseSectionView}
      size="100%"
      position="right"
      className={clsx(globalMobileWrapper, {
        [darkModeRoot]: isDarkMode,
      })}
      zIndex={MobileListingDrawerZIndex.ListingSectionalView}
    >
      <Drawer.Content className={drawerStyles.drawerContent}>
        <Drawer.Header className={drawerStyles.drawerHeader}>
          <MobileHeaderBar
            onBack={onCloseSectionView}
            title={
              <span
                style={{
                  textTransform: 'capitalize',
                  color: vars.color.textPrimary,
                }}
              >
                {activeTab.title}
              </span>
            }
          />
        </Drawer.Header>
        <Drawer.Body
          className={drawerStyles.drawerBody}
          style={{ backgroundColor: vars.color.backgroundPrimary }}
        >
          <Stack
            height="full"
            width="full"
            direction="column"
            gap="s"
            className={drawerStyles.drawerInnerContent}
          >
            <Paper
              style={{
                flex: 1,
                overflowY: 'auto',
                color: vars.color.textPrimary,
                backgroundColor: vars.color.backgroundPrimary,
              }}
            >
              {activeTab.content}
            </Paper>
            <Stack width="full" justifyContent="end">
              <OkButton
                shape="rect"
                onClick={onSubmit}
                textContentId={ContentId.Save}
                disabled={isLoading || !hasChanges}
              />
            </Stack>
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};
